import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * What deleting a deal would take with it, said out loud before the click.
 *
 * ── Why a deal needs a warning and a card does not ─────────────────────────
 *
 * Kyu, 1 Sep: "I need to be able to delete a deal... we got dupes we need to
 * fix." The gap was real — `deleteDeal` and `useDeleteDeal` have both existed
 * since Drop 33 and nothing ever called them.
 *
 * Reading every foreign key that points at `deals.id`:
 *
 *   tasks             ON DELETE CASCADE     follow-up work, in My Tasks
 *   task_comments     ON DELETE CASCADE     the conversation
 *   comment_mentions  ON DELETE CASCADE     the mentions
 *   cg_orders         ON DELETE SET NULL    the order SURVIVES, unlinked
 *
 * `tasks` cascades, and deal tasks reach My Tasks like any other task, so
 * deleting a deal silently removes work from other people's lists. The confirm
 * has to say the number out loud.
 *
 * `cg_orders` sets null. The order stays, pointing at nothing — and Kyu's rule
 * (1 Sep) is that this is CORRECT: "if we delete a deal, CG order should not be
 * affected. BUT we should be able to have the ability to create a new deal AND
 * associate that new deal to a CG order." `link_order_to_deal` (20270101000100)
 * is that way back, so an unlinked order is recoverable, not damage.
 *
 * ── Nothing here refuses — REVERSED AGAIN 1 Sep, and this time it was a bug ─
 *
 * Version one REFUSED a deal that carried a CG order. Kyu's rule above killed
 * that, and the refusal came out.
 *
 * What did NOT come out was the refusal on the check FAILING — "not knowing is
 * a refusal, never a default of safe." Kyu hit it immediately: the drawer read
 * "Could not check what is attached to this deal, so it cannot be deleted right
 * now" and the delete was unreachable. The board he needs to de-duplicate was
 * frozen by a guard.
 *
 * That guard was sound only while it guarded something. It does not any more.
 * If we cannot tell whether a CG order is attached, the order still survives
 * the delete and can still be re-attached — the cost of not knowing is one
 * missing sentence in the confirm, not a lost order. So the failure is
 * REPORTED, not enforced.
 *
 * ── What was actually failing: a column that does not exist ───────────────
 *
 * The task read asked for `tasks.status`. The table has `is_completed` and
 * `status_id` and has never had a `status` column, so PostgREST answered 400
 * on every deal, for every user — nothing to do with permissions, which is
 * where I first went looking. Reading the schema found it; guessing at RLS did
 * not. `is_completed` is the boolean that exists, and it is NOT NULL.
 *
 * ── And the three reads stop sharing a fate ────────────────────────────────
 *
 * They used to be one `Promise.all` behind one `throw`, so that single bad
 * column name took the CG order and comment counts down with it — and then the
 * refusal above took the whole delete control down with those. Three unrelated
 * failures, one cause, because everything shared a fate. Each read now fails on
 * its own and says so on its own.
 *
 * "Unknown" is never rendered as zero anywhere below. A count we could not
 * take is the one thing this file must not round down.
 */

export interface DealAttachments {
  /** CG orders pointing at this deal. They survive the delete, unlinked. */
  orderNos: string[];
  /**
   * The CG orders could not be read. `orderNos` is then empty because it is
   * UNKNOWN, not because there are none — never render it as "no orders".
   */
  ordersUnknown: boolean;
  /** Follow-up tasks that would be deleted with it. */
  openTasks: number;
  doneTasks: number;
  tasksUnknown: boolean;
  /** Comments that would go with it. */
  comments: number;
  commentsUnknown: boolean;
}

/** English for a count, so the confirm reads like a sentence rather than a log line. */
function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/**
 * The sentence that goes into the confirm, as a pure function of what is
 * attached. Empty when there is nothing to say, so a bare duplicate — the case
 * this whole feature exists for — gets a plain confirm rather than a warning
 * about nothing.
 *
 * There is no boolean here. Nothing refuses any more, and a field that is
 * always `true` is a lie waiting to be read as a real decision.
 */
export function deleteWarning(attached: DealAttachments): string {
  const parts: string[] = [];

  // Orders first, and never as a deletion. The worst thing this string could
  // do is imply the CG order goes with the deal. It does not.
  if (attached.ordersUnknown) {
    parts.push(
      'we could not check for CG orders — if one is attached it still stays, and can be attached to another deal afterwards',
    );
  } else if (attached.orderNos.length > 0) {
    const orders = attached.orderNos.join(', ');
    parts.push(
      attached.orderNos.length === 1
        ? `CG order ${orders} stays, but comes unlinked from this deal — you can attach it to another deal afterwards`
        : `CG orders ${orders} stay, but come unlinked from this deal — you can attach them to another deal afterwards`,
    );
  }

  if (attached.tasksUnknown) {
    parts.push('any follow-up tasks on this deal are deleted, and we could not count them');
  } else {
    const tasks = attached.openTasks + attached.doneTasks;
    if (tasks > 0) {
      // Open tasks are named separately because those are the ones sitting on
      // somebody's list right now. "3 tasks" reads as tidying up; "2 of them
      // still open" is the fact that makes a person stop and check.
      parts.push(
        attached.openTasks > 0
          ? `${count(tasks, 'follow-up task', 'follow-up tasks')} (${String(attached.openTasks)} still open, on somebody's list)`
          : count(tasks, 'finished follow-up task', 'finished follow-up tasks'),
      );
    }
  }

  if (attached.commentsUnknown) {
    parts.push('any comments on this deal are deleted, and we could not count them');
  } else if (attached.comments > 0) {
    parts.push(count(attached.comments, 'comment', 'comments'));
  }

  if (parts.length === 0) return '';
  // "This also deletes" is only true when every clause in the sentence IS a
  // deletion. An order clause is not one, and neither is an "unknown" clause.
  const everyPartIsADeletion =
    !attached.ordersUnknown &&
    attached.orderNos.length === 0 &&
    !attached.tasksUnknown &&
    !attached.commentsUnknown;
  const opener = everyPartIsADeletion ? 'This also deletes' : 'Also:';
  return `${opener} ${parts.join(', and ')}.`;
}

/**
 * What is hanging off one deal. Three reads, each allowed to fail alone.
 *
 * This never throws. A caller handed a thrown error had to choose between
 * treating it as "nothing attached" — the one reading that must never happen —
 * and refusing the delete, which is what froze the board. A shape that can say
 * "unknown" removes the choice.
 */
export async function fetchDealAttachments(
  client: SupabaseClient,
  dealId: string,
): Promise<DealAttachments> {
  const failed = { data: null, error: { message: 'unreachable' }, count: null };
  const [orders, tasks, comments] = await Promise.all([
    Promise.resolve(client.from('cg_orders').select('order_no').eq('deal_id', dealId)).catch(
      () => failed,
    ),
    // `is_completed`, NOT `status`. There is no `tasks.status` column and never
    // has been; asking for one 400s the request, which is the bug that took the
    // delete control off every deal drawer.
    Promise.resolve(client.from('tasks').select('id, is_completed').eq('deal_id', dealId)).catch(
      () => failed,
    ),
    Promise.resolve(
      client
        .from('task_comments')
        .select('id', { count: 'exact', head: true })
        .eq('deal_id', dealId),
    ).catch(() => failed),
  ]);

  const ordersUnknown = orders.error !== null;
  const tasksUnknown = tasks.error !== null;
  const commentsUnknown = comments.error !== null;
  const rows = tasks.data ?? [];

  return {
    // Empty on failure, and `ordersUnknown` beside it is what stops that
    // emptiness ever being read as "there are none".
    orderNos: ordersUnknown ? [] : (orders.data ?? []).map((row) => String(row.order_no)),
    ordersUnknown,
    openTasks: tasksUnknown ? 0 : rows.filter((row) => row.is_completed !== true).length,
    doneTasks: tasksUnknown ? 0 : rows.filter((row) => row.is_completed === true).length,
    tasksUnknown,
    comments: commentsUnknown ? 0 : (comments.count ?? 0),
    commentsUnknown,
  };
}

/**
 * Everything unknown. What the drawer falls back to if the query itself never
 * produced a result — the delete still works, and the confirm still tells the
 * truth about what it does not know.
 */
export const NOTHING_KNOWN: DealAttachments = {
  orderNos: [],
  ordersUnknown: true,
  openTasks: 0,
  doneTasks: 0,
  tasksUnknown: true,
  comments: 0,
  commentsUnknown: true,
};

export function useDealAttachments(dealId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['deals', 'attachments', dealId],
    queryFn: () => fetchDealAttachments(client, dealId),
    retry: false,
  });
}
